import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  Link,
} from 'react-router';
import {
  get,
  forEach,
} from 'lodash';
import {
  API,
  NavigationHelper,
  ToastActions,
  Field2 as Field,
  VibeModal,
  VibeTooltip,
  VibeIcon,
  viArrowRight,
  viEye,
  viLock,
  viNowPlaying,
  viCalendar,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import './AdProgramListItem.scss';

let effectiveDatesChanged = false;

function AdProgramListItem({
  className = '',
  adProgramId,
  locationId,
  startDate: propsStartDate,
  endDate: propsEndDate,
  selected = false,
  current = false,
  future = false,
  obsolete = false,
  onlyAdProgram = false,
  futureMostAdProgram = false,
  queueToast,
  onUpdateAdProgram = () => {},
}) {
  const [showEffectiveDates, setShowEffectiveDates] = useState(false);
  const [startDate, setStartDate] = useState(moment(propsStartDate).format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment(propsEndDate).format('YYYY-MM-DD'));
  const [effectiveStartDate, setEffectiveStartDate] = useState(null);
  const [effectiveEndDate, setEffectiveEndDate] = useState(null);
  const [allowStartDateChange, setAllowStartDateChange] = useState(false);
  const [allowEndDateChange, setAllowEndDateChange] = useState(false);

  /**
   * Change start date
   */
  const onChangeStartDate = (date) => {
    setStartDate(date);
  };

  /**
   * Change end date
   */
  const onChangeEndDate = (date) => {
    setEndDate(date);
  };

  /**
   * Change effective start date
   */
  const onChangeEffectiveStartDate = (date) => {
    setEffectiveStartDate(date);
    effectiveDatesChanged = true;
  };

  /**
   * Change effective end date
   */
  const onChangeEffectiveEndDate = (date) => {
    setEffectiveEndDate(date);
    effectiveDatesChanged = true;
  };

  /**
   * Clicking the effective dates icon
   */
  const onClickEffectiveDates = (e) => {
    e.preventDefault();
    setShowEffectiveDates(true);
  };

  /**
   * Cancel the effective dates
   */
  const onCancelEffectiveDates = () => {
    setShowEffectiveDates(false);
  };

  /**
   * Save the effective dates
   */
  const onSaveEffectiveDates = async () => {
    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    try {
      const data = {};

      if (allowStartDateChange && propsStartDate !== startDate) {
        data.startDate = startDate;
      }

      if (allowEndDateChange && propsEndDate !== endDate) {
        data.endDate = endDate;
      }

      if ((allowStartDateChange || allowEndDateChange) && (propsStartDate !== startDate || propsEndDate !== endDate)) {
        const updateDatesResponse = await API.Location.AdPrograms.update({
          locationId,
          adProgramId,
          _id: adProgramId,
          ...data,
        });

        const updateDatesSuccess = get(updateDatesResponse, '[0].type') === 'LOCATIONADPROGRAM.UPDATED';

        if (updateDatesSuccess) {
          queueToast({
            type: 'success',
            title: 'Saved Dates!',
            allowClose: true,
          });

          effectiveDatesChanged = false;
          onUpdateAdProgram();
        } else {
          console.error('Error saving dates to API', updateDatesResponse);

          queueToast({
            type: 'error',
            title: 'Error Saving Dates',
            timeout: 10,
            allowClose: true,
          });
        }
      }

      if (effectiveDatesChanged) {
        const response = await API.Location.AdPrograms.updateEffectiveDates({
          locationId,
          adProgramId,
          _id: adProgramId,
          effectiveStartDate,
          effectiveEndDate,
        });

        const success = get(response, '[0].type') === 'LOCATIONADPROGRAM.EFFECTIVE_DATES_UPDATED';

        if (success) {
          queueToast({
            type: 'success',
            title: 'Saved Effective Dates!',
            allowClose: true,
          });

          effectiveDatesChanged = false;
        } else {
          console.error('Error saving effective dates to API', response);

          queueToast({
            type: 'error',
            title: 'Error Saving Dates',
            timeout: 10,
            allowClose: true,
          });
        }
      }
    } catch (err) {
      console.error('Error saving dates', err);

      queueToast({
        type: 'error',
        title: 'Error Saving Dates',
        timeout: 10,
        allowClose: true,
      });
    }

    setShowEffectiveDates(false);
  };

  useEffect(() => {
    if (showEffectiveDates) {
      async function fetchData() {
        // fetch the ad program to get the effective start/end dates
        const adProgram = await API.Location.AdPrograms.get({
          locationId,
          adProgramId,
        });

        if (onlyAdProgram) {
          // if this is the only ad program, allow both dates to be changed
          // as long as it has no allocations or day parts associated with it
          let hasSpotPools = false;
          let hasSpotValues = false;

          forEach(adProgram.spotPools, (spotPools) => {
            if (spotPools.length > 0) {
              hasSpotPools = true;
            }
          });

          forEach(adProgram.spotValues, (spotValues) => {
            // spot values that aren't zero (indicating there are spots allocated)
            const notZeroSpotValues = spotValues.filter(spotValue => spotValue !== 0);
            
            if (notZeroSpotValues.length > 0) {
              hasSpotValues = true;
            }
          });

          if (!hasSpotPools && !hasSpotValues) {
            setAllowStartDateChange(true);
            setAllowEndDateChange(true);
          }
        }

        if (futureMostAdProgram) {
          // if this is the future most ad program, allow end date to be changed
          // no other ad programs can exist starting after this one
          setAllowEndDateChange(true);
        }

        setEffectiveStartDate(adProgram.effectiveStartDate);
        setEffectiveEndDate(adProgram.effectiveEndDate);
      }
      fetchData();
    }
  }, [showEffectiveDates]);

  return (
    <div>
      <VibeTooltip
        title={`${propsStartDate} to ${propsEndDate}`}
        placement="right"
      >
        <Link
          className={classNames('AdProgramListItem', className, { selected })}
          to={NavigationHelper.updateParams({
            adProgramId,
            day: null,
            spotPoolStart: null,
          })}
          data-testid={`ad-program-list-item-${adProgramId}`}
        >
          {propsStartDate}

          <VibeIcon
            icon={viArrowRight}
            color={color.manatee}
            size={16}
            style={{
              margin: '0 4px',
            }}
          />

          <div className="end-date">
            {propsEndDate}
          </div>

          {obsolete && (
            <VibeIcon
              icon={viEye}
              color={color.manatee}
              size={16}
              style={{
                margin: '0 4px',
              }}
              tooltip="This ad program is obsolete"
            />
          )}

          {current && (
            <VibeIcon
              icon={viNowPlaying}
              color={color.manatee}
              size={16}
              style={{
                margin: '0 4px',
              }}
              tooltip="Active Ad Program"
            />
          )}

          <VibeIcon
            icon={viCalendar}
            color={color.manatee}
            hoverColor={color.obsidian}
            size={16}
            style={{
              margin: '0 4px',
            }}
            tooltip="Effective Dates"
            disabled={obsolete}
            onClick={onClickEffectiveDates}
            data-testid={`ad-program-list-item-${adProgramId}-effective-dates-button`}
          />

          {!future && (
            <VibeIcon
              icon={viLock}
              color={color.manatee}
              size={16}
              style={{
                margin: '0 4px',
              }}
              tooltip="Only Future Ad Programs can be Modified"
            />
          )}
        </Link>
      </VibeTooltip>

      <VibeModal
        type="confirm"
        show={showEffectiveDates}
        title="Ad Program Dates"
        testIds={{
          root: 'ad-program-effective-dates-dialog',
          confirm: 'ad-program-effective-dates-confirm-button',
          cancel: 'ad-program-effective-dates-cancel-button',
        }}
        onConfirm={onSaveEffectiveDates}
        onClose={onCancelEffectiveDates}
      >
        <Grid spacing={2} container>
          <Grid size={6}>
            {allowStartDateChange ? (
              <Field
                type="datepicker"
                label="Start Date"
                placeholder="YYYY-MM-DD"
                value={startDate}
                dateProps={{
                  format: 'YYYY-MM-DD',
                }}
                testId="ad-program-effective-dates-start-date-input"
                onChange={onChangeStartDate}
              />
            ) : (
              <Field
                type="custom"
                label="Start Date"
              >
                {moment(startDate).format('YYYY-MM-DD')}
              </Field>
            )}
          </Grid>

          <Grid size={6}>
            {allowEndDateChange ? (
              <Field
                type="datepicker"
                label="End Date"
                placeholder="YYYY-MM-DD"
                value={endDate}
                dateProps={{
                  format: 'YYYY-MM-DD',
                }}
                testId="ad-program-effective-dates-end-date-input"
                onChange={onChangeEndDate}
              />
            ) : (
              <Field
                type="custom"
                label="End Date"
              >
                {moment(endDate).format('YYYY-MM-DD')}
              </Field>
            )}
          </Grid>

          <Grid size={12}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 'bold',
              }}
            >
              Reporting Effective Dates
            </div>
          </Grid>

          <Grid size={6}>
            <Field
              type="datepicker"
              label="Start Date"
              placeholder="YYYY-MM-DD"
              value={effectiveStartDate}
              dateProps={{
                format: 'YYYY-MM-DD',
                minDate: moment(startDate).toDate(),
                maxDate: moment(endDate).toDate(),
              }}
              testId="ad-program-effective-dates-reporting-start-date-input"
              onChange={onChangeEffectiveStartDate}
              required
            />
          </Grid>

          <Grid size={6}>
            <Field
              type="datepicker"
              label="End Date"
              placeholder="YYYY-MM-DD"
              value={effectiveEndDate}
              dateProps={{
                format: 'YYYY-MM-DD',
                minDate: moment(startDate).toDate(),
                maxDate: moment(endDate).toDate(),
              }}
              testId="ad-program-effective-dates-reporting-end-date-input"
              onChange={onChangeEffectiveEndDate}
              required
            />
          </Grid>
        </Grid>
      </VibeModal>
    </div>
  );
}

AdProgramListItem.propTypes = {
  className: PropTypes.string,
  adProgramId: PropTypes.string.isRequired,
  locationId: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  current: PropTypes.bool,
  future: PropTypes.bool,
  obsolete: PropTypes.bool,
};

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
};

export default connect(null, mapDispatchToProps)(AdProgramListItem);
